import { DateTimePicker12 } from './TimePicker12';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { listLeads, getLead, createLead, updateLead, setLeadStatus, addLeadInteraction, deleteLead } from '../api';
import TileSelect from './TileSelect';
import LocalityAutocomplete from './LocalityAutocomplete';

// Docs/requirements/hospitality/GoaVisitPlan/v1_chatgppt_plan — the website owner's ONE page to
// manage onboarding leads: every call / WhatsApp message / site visit / discussion with a
// prospective business owner, with pagination, and an open/close toggle per discussion.

const STAGES = [
  ['new', 'New', 'secondary'],
  ['contacted', 'Contacted', 'info'],
  ['interested', 'Interested', 'primary'],
  ['qualified', 'Qualified', 'primary'],
  ['appointment_scheduled', 'Appointment scheduled', 'warning'],
  ['onboarded', 'Onboarded', 'success'],
  ['rejected', 'Rejected', 'dark']
];
const stageLabel = (s) => STAGES.find(([k]) => k === s)?.[1] || s;
const stageVariant = (s) => STAGES.find(([k]) => k === s)?.[2] || 'secondary';

// 'demo_request' is written automatically (routes/platformAdmin.js, when the lead was spawned
// from an inbound "Request a Demo" submission) — kept in the lookup so the history renders a
// proper label, but left OUT of the manually-loggable list below.
const INTERACTION_KINDS = [
  ['call', '📞 Call'], ['whatsapp', '💬 WhatsApp'], ['visit', '🚗 Visit'],
  ['email', '✉️ Email'], ['meeting', '🤝 Meeting'], ['note', '📝 Note'],
  ['demo_request', '🎥 Demo request']
];
const LOGGABLE_INTERACTION_KINDS = INTERACTION_KINDS.filter(([k]) => k !== 'demo_request');
const OUTCOME_SUGGESTIONS = ['no_answer', 'interested', 'not_interested', 'follow_up', 'scheduled', 'completed'];

const VERIFICATION_LEVELS = [
  ['none', 'Not verified'], ['remote', 'Remote'], ['video', 'Video'], ['physical', 'Physical']
];

// Quick-pick tiles for a fast add (leads_business_flow_requirement.md §1.1/§1.2, generalised to
// real estate) — "Other…" still takes free text since neither field is a backend enum.
const BUSINESS_TYPE_TILES = [
  'Independent agent', 'Real estate agency', 'Builder / developer',
  'Property management company', 'Broker network', 'Co-working / commercial specialist'
];
const SOURCE_TILES = [
  'Google Maps search', 'Builder network', 'Property portal', 'Referral', 'Walk-in', 'Website enquiry'
];

const emptyLeadForm = {
  businessName: '', contactName: '', phone: '', whatsapp: '', email: '',
  area: '', areaLat: null, areaLng: null, areaPlaceId: '',
  propertyType: '', unitsCount: '', source: '', notes: ''
};

function fmt(d) {
  return d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
}
function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

export default function LeadsPanel() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, hasMore: false });
  const [summary, setSummary] = useState({ open: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [status, setStatus] = useState('open');
  const [stage, setStage] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [dueFollowUps, setDueFollowUps] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyLeadForm);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState('');

  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listLeads({ status, stage: stage || undefined, q: q || undefined, page, limit: 20, dueFollowUps: dueFollowUps || undefined });
      setRows(res.data);
      setPagination(res.pagination);
      setSummary(res.summary);
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not load leads.');
    } finally {
      setLoading(false);
    }
  }, [status, stage, q, page, dueFollowUps]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, stage, q, dueFollowUps]);

  const submitAdd = async () => {
    setAddBusy(true);
    setAddError('');
    try {
      await createLead({
        ...addForm,
        unitsCount: addForm.unitsCount === '' ? undefined : Number(addForm.unitsCount)
      });
      setShowAdd(false);
      setAddForm(emptyLeadForm);
      load();
    } catch (e) {
      setAddError(e.response?.data?.msg || 'Could not add this lead.');
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="mb-1">Onboarding leads</h5>
          <div className="small text-muted">
            {summary.open ?? 0} open discussion{(summary.open ?? 0) === 1 ? '' : 's'} · {summary.closed ?? 0} closed
          </div>
        </div>
        <Button size="sm" onClick={() => { setAddForm(emptyLeadForm); setAddError(''); setShowAdd(true); }}>+ Add lead</Button>
      </div>

      <Row className="g-2 mb-3 align-items-end">
        <Col xs={6} md={2}>
          <Form.Label className="small mb-0">Status</Form.Label>
          <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </Form.Select>
        </Col>
        <Col xs={6} md={3}>
          <Form.Label className="small mb-0">Stage</Form.Label>
          <Form.Select size="sm" value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">All stages</option>
            {STAGES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </Form.Select>
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="small mb-0">Search</Form.Label>
          <Form.Control size="sm" placeholder="Business, contact, area, phone…" value={q} onChange={(e) => setQ(e.target.value)} />
        </Col>
        <Col xs={12} md={3}>
          <Form.Check
            type="switch" id="due-followups" label="Follow-ups due" size="sm"
            checked={dueFollowUps} onChange={(e) => setDueFollowUps(e.target.checked)}
          />
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {loading ? <Spinner animation="border" size="sm" role="status" /> : rows.length === 0 ? (
        <Alert variant="light">No leads match these filters.</Alert>
      ) : (
        <>
          <Table responsive hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Business</th><th>Contact</th><th>Stage</th><th>Status</th>
                <th>Last activity</th><th>Next follow-up</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setDetailId(r.id)}>
                  <td>
                    <strong>{r.businessName}</strong>
                    {r.sourceDemoRequestId && <Badge bg="light" text="dark" className="border ms-1">🎥 Demo request</Badge>}
                    {r.area ? <div className="small text-muted">{r.area}</div> : null}
                  </td>
                  <td className="small">
                    {r.contactName || '—'}
                    {r.phone ? <div className="text-muted">{r.phone}</div> : null}
                  </td>
                  <td><Badge bg={stageVariant(r.stage)}>{stageLabel(r.stage)}</Badge></td>
                  <td><Badge bg={r.status === 'open' ? 'success' : 'secondary'}>{r.status}</Badge></td>
                  <td className="small">{fmt(r.lastActivityAt)}<div className="text-muted">{r.interactionCount} logged</div></td>
                  <td className="small">{r.nextFollowUpAt ? fmt(r.nextFollowUpAt) : '—'}</td>
                  <td>
                    <Button size="sm" variant="outline-secondary" onClick={(e) => { e.stopPropagation(); setDetailId(r.id); }}>Open</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between align-items-center">
            <span className="small text-muted">
              Page {pagination.page} of {Math.max(1, Math.ceil(pagination.total / pagination.limit))} · {pagination.total} total
            </span>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" disabled={pagination.page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</Button>
              <Button size="sm" variant="outline-secondary" disabled={!pagination.hasMore} onClick={() => setPage((p) => p + 1)}>Next ›</Button>
            </div>
          </div>
        </>
      )}

      {/* Add lead */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Add an onboarding lead</Modal.Title></Modal.Header>
        <Modal.Body>
          {addError && <Alert variant="danger">{addError}</Alert>}
          <Form.Group className="mb-2">
            <Form.Label>Agency / business name</Form.Label>
            <Form.Control value={addForm.businessName} onChange={(e) => setAddForm((f) => ({ ...f, businessName: e.target.value }))} />
          </Form.Group>
          <Row>
            <Col sm={6}>
              <Form.Group className="mb-2">
                <Form.Label>Contact name</Form.Label>
                <Form.Control value={addForm.contactName} onChange={(e) => setAddForm((f) => ({ ...f, contactName: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-2">
                <Form.Label>Properties managed</Form.Label>
                <Form.Control type="number" min={0} value={addForm.unitsCount} onChange={(e) => setAddForm((f) => ({ ...f, unitsCount: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-2">
                <Form.Label>Phone</Form.Label>
                <Form.Control value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-2">
                <Form.Label>WhatsApp</Form.Label>
                <Form.Control value={addForm.whatsapp} onChange={(e) => setAddForm((f) => ({ ...f, whatsapp: e.target.value }))} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Area / locality</Form.Label>
            <LocalityAutocomplete
              idPrefix="add-lead-area"
              value={addForm.area}
              onChange={({ area, areaLat, areaLng, areaPlaceId }) => setAddForm((f) => ({ ...f, area, areaLat, areaLng, areaPlaceId }))}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Business type</Form.Label>
            <TileSelect
              idPrefix="add-lead-proptype"
              options={BUSINESS_TYPE_TILES}
              value={addForm.propertyType}
              onChange={(propertyType) => setAddForm((f) => ({ ...f, propertyType }))}
              otherPlaceholder="Type the property type…"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Source</Form.Label>
            <TileSelect
              idPrefix="add-lead-source"
              options={SOURCE_TILES}
              value={addForm.source}
              onChange={(source) => setAddForm((f) => ({ ...f, source }))}
              otherPlaceholder="Where did this lead come from?"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Notes</Form.Label>
            <Form.Control as="textarea" rows={2} value={addForm.notes} onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button onClick={submitAdd} disabled={addBusy || !addForm.businessName.trim()}>{addBusy ? 'Adding…' : 'Add lead'}</Button>
        </Modal.Footer>
      </Modal>

      <LeadDetailModal
        leadId={detailId}
        rows={rows}
        onHide={() => setDetailId(null)}
        onChanged={load}
      />
    </div>
  );
}

function LeadDetailModal({ leadId, rows, onHide, onChanged }) {
  const [lead, setLead] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [interKind, setInterKind] = useState('call');
  const [interOutcome, setInterOutcome] = useState('');
  const [interNotes, setInterNotes] = useState('');
  const [interFollowUp, setInterFollowUp] = useState('');

  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  useEffect(() => {
    if (!leadId) { setLead(null); return; }
    // Seed instantly from the list row (already loaded), then refresh with full detail.
    setLead(rows.find((r) => r.id === leadId) || null);
    setError('');
    setInterKind('call'); setInterOutcome(''); setInterNotes(''); setInterFollowUp(''); setClosing(false); setCloseReason('');
    getLead(leadId).then(setLead).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  if (!leadId || !lead) return null;

  const logInteraction = async () => {
    setBusy(true);
    setError('');
    try {
      const updated = await addLeadInteraction(leadId, {
        kind: interKind, outcome: interOutcome || undefined, notes: interNotes || undefined,
        nextFollowUpAt: interFollowUp || undefined
      });
      setLead(updated);
      setInterOutcome(''); setInterNotes(''); setInterFollowUp('');
      onChanged();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not log that interaction.');
    } finally {
      setBusy(false);
    }
  };

  const toggleFollowUps = async () => {
    setBusy(true); setError('');
    try {
      const updated = await updateLead(leadId, { followUpsStopped: !lead.followUpsStopped });
      setLead(updated); onChanged();
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not change follow-ups.');
    } finally { setBusy(false); }
  };

  const changeStage = async (nextStage) => {
    try {
      const updated = await updateLead(leadId, { stage: nextStage });
      setLead(updated);
      onChanged();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not update the stage.');
    }
  };

  const changeVerification = async (level) => {
    try {
      const updated = await updateLead(leadId, { verificationLevel: level });
      setLead(updated);
      onChanged();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not update verification.');
    }
  };

  const submitClose = async () => {
    setBusy(true);
    setError('');
    try {
      const updated = await setLeadStatus(leadId, { status: 'closed', closedReason: closeReason });
      setLead(updated);
      setClosing(false);
      onChanged();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not close this discussion.');
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    try {
      const updated = await setLeadStatus(leadId, { status: 'open' });
      setLead(updated);
      onChanged();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not reopen this discussion.');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the lead "${lead.businessName}"? This can't be undone.`)) return;
    try {
      await deleteLead(leadId);
      onChanged();
      onHide();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not delete this lead.');
    }
  };

  return (
    <Modal show={!!leadId} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {lead.businessName}{' '}
          <Badge bg={lead.status === 'open' ? 'success' : 'secondary'} className="ms-1">{lead.status}</Badge>
          {lead.sourceDemoRequestId && <Badge bg="light" text="dark" className="border ms-1">🎥 From a demo request</Badge>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        <div className="mb-3">
          <Button size="sm" disabled={busy} variant={lead.followUpsStopped ? 'outline-primary' : 'outline-danger'} onClick={toggleFollowUps}>
            {lead.followUpsStopped ? 'Resume follow-ups' : 'Stop follow-ups'}
          </Button>
          <div className="small text-muted mt-1">
            {lead.followUpsStopped ? 'Automatic follow-up emails and WhatsApp reminders are stopped.' : 'Stop automatic reminders without closing this lead.'}
          </div>
        </div>

        <Row className="small mb-3">
          <Col md={6}>
            <div><span className="text-muted">Contact:</span> {lead.contactName || '—'}</div>
            <div><span className="text-muted">Phone:</span> {lead.phone || '—'} {lead.whatsapp ? `· WhatsApp ${lead.whatsapp}` : ''}</div>
            <div><span className="text-muted">Email:</span> {lead.email || '—'}</div>
            <div><span className="text-muted">Area:</span> {lead.area || '—'}</div>
          </Col>
          <Col md={6}>
            <div><span className="text-muted">Type:</span> {lead.propertyType || '—'} {lead.unitsCount ? `· ${lead.unitsCount} units` : ''}</div>
            <div><span className="text-muted">Source:</span> {lead.source || '—'}</div>
            <div><span className="text-muted">Assigned to:</span> {lead.assignedToName || '—'}</div>
            <div><span className="text-muted">Added:</span> {fmtDate(lead.createdAt)} by {lead.createdByName || '—'}</div>
          </Col>
        </Row>

        <Row className="g-2 mb-3">
          <Col sm={6}>
            <Form.Label className="small mb-0">Stage</Form.Label>
            <Form.Select size="sm" value={lead.stage} onChange={(e) => changeStage(e.target.value)}>
              {STAGES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </Form.Select>
          </Col>
          <Col sm={6}>
            <Form.Label className="small mb-0">Verification</Form.Label>
            <Form.Select size="sm" value={lead.verificationLevel} onChange={(e) => changeVerification(e.target.value)}>
              {VERIFICATION_LEVELS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </Form.Select>
          </Col>
        </Row>

        {lead.status === 'closed' ? (
          <Alert variant="secondary" className="py-2">
            Closed {fmt(lead.closedAt)} — {lead.closedReason}
            {' '}<Button size="sm" variant="outline-primary" className="ms-2" onClick={reopen}>Reopen discussion</Button>
          </Alert>
        ) : closing ? (
          <div className="border rounded p-2 mb-3">
            <Form.Label className="small mb-1">Reason for closing</Form.Label>
            <Form.Control size="sm" as="textarea" rows={2} value={closeReason} onChange={(e) => setCloseReason(e.target.value)} placeholder="e.g. onboarded / not interested / unreachable / duplicate" />
            <div className="d-flex gap-2 mt-2">
              <Button size="sm" variant="secondary" disabled={busy || !closeReason.trim()} onClick={submitClose}>Confirm close</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setClosing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="outline-secondary" className="mb-3" onClick={() => setClosing(true)}>Close discussion</Button>
        )}

        <h6 className="text-uppercase text-muted small">Log a call / visit / discussion</h6>
        <Row className="g-2 mb-2">
          <Col sm={4}>
            <Form.Select size="sm" value={interKind} onChange={(e) => setInterKind(e.target.value)}>
              {LOGGABLE_INTERACTION_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </Form.Select>
          </Col>
          <Col sm={4}>
            <Form.Control size="sm" list="lead-outcomes" placeholder="Outcome (optional)" value={interOutcome} onChange={(e) => setInterOutcome(e.target.value)} />
            <datalist id="lead-outcomes">{OUTCOME_SUGGESTIONS.map((o) => <option key={o} value={o} />)}</datalist>
          </Col>
          <Col sm={4}>
            <DateTimePicker12 size="sm"  value={interFollowUp} onChange={(e) => setInterFollowUp(e.target.value)} title="Next follow-up (optional)" />
          </Col>
        </Row>
        <Form.Control
          as="textarea" rows={2} className="mb-1" placeholder="What was discussed?"
          value={interNotes} onChange={(e) => setInterNotes(e.target.value)}
        />
        <div className="small text-muted mb-2">
          Leave &quot;Next follow-up&quot; blank and mention a day/time in the note (e.g.
          &quot;call me on Monday&quot;, &quot;follow up tomorrow at 3pm&quot;) — it&apos;s picked
          up automatically and you&apos;ll get a WhatsApp/email reminder before the call.
        </div>
        <Button size="sm" disabled={busy} onClick={logInteraction} className="mb-3">Log interaction</Button>

        <h6 className="text-uppercase text-muted small">History ({lead.interactions?.length || 0})</h6>
        {(!lead.interactions || lead.interactions.length === 0) ? (
          <div className="small text-muted">Nothing logged yet.</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {[...lead.interactions].reverse().map((i) => (
              <div key={i._id} className="border rounded p-2 small">
                <div className="d-flex justify-content-between">
                  <strong>{INTERACTION_KINDS.find(([k]) => k === i.kind)?.[1] || i.kind}{i.outcome ? ` · ${i.outcome}` : ''}</strong>
                  <span className="text-muted">{fmt(i.at)}{i.byName ? ` · ${i.byName}` : ''}</span>
                </div>
                {i.notes && <div className="mt-1">{i.notes}</div>}
                {i.nextFollowUpAt && (
                  <div className="text-muted mt-1">
                    Next follow-up: {fmt(i.nextFollowUpAt)}
                    {i.nextFollowUpAuto && (
                      <Badge bg="light" text="dark" className="border ms-1" title="Detected from the note text">
                        🔎 auto-detected
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-danger" size="sm" onClick={remove}>Delete lead</Button>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
